import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { T } from "@/constants/testIds";

const LINKS = [
  { to: "/books", label: "Books" },
  { to: "/news", label: "News" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/[0.07]" : "bg-transparent"
      }`}
      data-testid={T.nav}
    >
      <div className="mx-auto flex max-w-[1440px] items-center justify-between px-5 py-4 sm:px-8 lg:px-14">
        <Link to="/" className="group flex items-baseline gap-2" data-testid="nav-brand-link">
          <span className="font-serif text-xl tracking-wide text-ink sm:text-2xl">
            Isabella <span className="serif-italic text-cyanx">MacLeod</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-10 md:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-sweep font-mono text-[11px] uppercase tracking-[0.28em] transition-colors ${
                  isActive ? "text-cyanx" : "text-dim hover:text-ink"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/books"
            data-testid="nav-cta-trilogy"
            className="rounded-full border border-cyanx/40 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.24em] text-cyanx transition-all duration-300 hover:bg-cyanx hover:text-void"
          >
            The Trilogy
          </Link>
        </nav>

        <button
          className="rounded-md p-2 text-ink md:hidden"
          onClick={() => setOpen((v) => !v)}
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid={T.navToggle}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="glass fixed inset-x-0 top-[64px] z-40 border-b border-white/[0.07] md:hidden"
            data-testid={T.navMobileMenu}
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {[...LINKS, { to: "/books", label: "The Trilogy" }].map((l, i) => (
                <NavLink
                  key={i}
                  to={l.to}
                  className={({ isActive }) =>
                    `border-b border-white/[0.06] py-4 font-serif text-3xl ${
                      isActive ? "text-cyanx" : "text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
